import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Lightbulb, Search } from 'lucide-react';
import type {
  AnalyticsDashboard,
  KnowledgeGap,
} from '@services/knowledge-analytics.service';
import { CARD, CARD_CLASS, CardFoot } from './shell';
import { Notice, Pager } from '@shared/ui/kit';
import { MetricCard } from './InsightsStats';

// 12c — Insights: the gap queue.
//
// This is the real screen from phase 25 §4, not phase 29's degraded coverage
// report. Phase 30's Task 0 question 7 asks whether questions are logged
// anywhere, and says: "If the answer to 7 is yes, stop — you can build the real
// gap queue." Here the answer is yes — `kb_search_logs` plus thirteen
// gap/search endpoints — so this is the designed screen.
//
// What it is for: the editorial backlog nobody currently has. Every other KB
// screen tells you about articles that exist; this one is the only place that
// says what people asked for and did not get.

const MONO = 'font-mono';

const W = { times: 96, lastAsked: 110, action: 196 } as const;

/**
 * How many gaps to ask for.
 *
 * 100 IS THE API'S CEILING, NOT A PREFERENCE — `SearchAnalyticsQueryDto.limit`
 * carries `@Max(100)`, and asking for more is a 400, not a longer list. (This
 * is the second time that exact ceiling has been walked into with the number
 * 200; see the customers page-size bug.)
 *
 * Within that ceiling, take all of it. The number must not be small enough to
 * look like an answer: the screen this replaced fetched `limit: 10` and then
 * printed the row count as "Open Gaps 10", while the trend line beside it read
 * the real figure off a different endpoint and said 12. Four numbers on that
 * panel were the page size wearing a label.
 *
 * `identifyGaps` clusters every failed search first and slices last, so the
 * database work is identical whatever this is; the limit only truncates the
 * response array. `gaps.length >= GAP_FETCH_LIMIT` below is what stops the
 * count asserting completeness if the ceiling is ever reached.
 */
export const GAP_FETCH_LIMIT = 100;

/** Gaps per page of the queue. The cards above still count every gap loaded. */
export const GAP_PAGE_SIZE = 20;

/** The thresholds already used by this codebase's dashboard — not new ones. */
function bandOf(priority: number): 'high' | 'medium' | 'low' {
  if (priority >= 70) return 'high';
  if (priority > 40) return 'medium';
  return 'low';
}

function shortDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function relative(iso?: string): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const days = Math.floor((Date.now() - t) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return shortDate(iso);
}

/**
 * §4.1 — the four gap figures.
 *
 * These are the ORIGINAL Knowledge Analytics cards — Open Gaps, High Priority,
 * Resolved, Total Searches — on the same `MetricCard` the Overview section
 * uses. They replace the ink-coloured metric strip that stood here, which was a
 * second vocabulary for "here is a number" on a screen that already had one,
 * and which reported figures the Overview tab was already showing.
 *
 * EVERY NUMBER IS DRAWN FROM THE ROWS BELOW IT, not from the dashboard
 * aggregate, because a card that disagrees with the list under it is worse than
 * no card. The strip's `SEARCHES` was `search.totalSearches` — every logged
 * search in the period, most of which found what they were looking for — while
 * this card's Total Searches is the searches that actually opened these gaps.
 * They are different quantities and the old screen showed the wrong one here.
 *
 * `Resolved` is the one figure the list cannot supply (a resolved gap is not in
 * it), so it takes the larger of the period aggregate and whatever resolved
 * rows did come back — the same expression the original used.
 */
export function GapKpis({
  dashboard,
  gaps,
  days,
  capped = false,
}: {
  dashboard: AnalyticsDashboard | null;
  gaps: KnowledgeGap[];
  days: number;
  /** The fetch hit its ceiling, so these are counts of a page, not a backlog. */
  capped?: boolean;
}) {
  const open = gaps.filter((g) => g.status !== 'resolved');
  const highPriority = open.filter((g) => bandOf(g.priority) === 'high').length;
  const resolved = Math.max(
    dashboard?.gaps?.resolvedThisPeriod ?? 0,
    gaps.filter((g) => g.status === 'resolved').length,
  );
  const asked = open.reduce((sum, g) => sum + (g.searchCount ?? 0), 0);

  return (
    <div className="grid grid-cols-2 gap-[14px] md:grid-cols-4">
      <MetricCard
        title="Open Gaps"
        value={capped ? `${open.length}+` : open.length}
        subtitle={capped ? 'First page — more are open' : 'Unanswered and not dismissed'}
        icon={<Lightbulb className="h-[17px] w-[17px]" />}
        accent="brass"
      />
      <MetricCard
        title="High Priority"
        value={highPriority}
        subtitle="Priority 70 or above"
        icon={<AlertTriangle className="h-[17px] w-[17px]" />}
        accent={highPriority > 0 ? 'burgundy' : 'terracotta'}
      />
      <MetricCard
        title="Resolved"
        value={resolved}
        subtitle={`Closed in the last ${days} days`}
        icon={<CheckCircle2 className="h-[17px] w-[17px]" />}
        accent="green"
      />
      <MetricCard
        title="Total Searches"
        value={asked.toLocaleString()}
        subtitle={capped ? 'Asked across this page of gaps' : 'Asked across the gaps below'}
        icon={<Search className="h-[17px] w-[17px]" />}
        accent="machine"
        note="The searches that opened these gaps — not every search logged this period, which the Overview tab reports separately."
      />
    </div>
  );
}

function PriorityChip({ band }: { band: 'high' | 'medium' | 'low' }) {
  if (band === 'low') return null;
  const style =
    band === 'high'
      ? {
          borderColor: 'var(--co-burgundy-line)',
          background: 'var(--co-burgundy-wash)',
          color: 'var(--co-burgundy)',
        }
      : { borderColor: 'var(--co-terracotta)', color: 'var(--co-terracotta-ink)' };
  return (
    <span
      className={`${MONO} flex-none border px-[6px] py-[2px] text-[9.5px] font-semibold tracking-[.12em]`}
      style={style}
    >
      {band.toUpperCase()}
    </span>
  );
}

function ColumnHead({ children, width }: { children?: ReactNode; width?: number }) {
  return (
    <div
      role="columnheader"
      className={`${MONO} ${width ? 'flex-none text-right' : 'min-w-0 flex-1'} text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-ink-4)]`}
      style={width ? { width } : undefined}
    >
      {children}
    </div>
  );
}

export function GapQueue({
  dashboard,
  gaps,
  days,
  error,
  onDraft,
  onExtend,
  onDismiss,
  hasMore,
  onLoadMore,
  loadingMore = false,
}: {
  dashboard: AnalyticsDashboard | null;
  gaps: KnowledgeGap[];
  /** The reporting period, for the Resolved card's subtitle. */
  days: number;
  /** Why the gap list could not be loaded, if it could not be. */
  error?: string | null;
  onDraft: (gap: KnowledgeGap) => void;
  onExtend: (gap: KnowledgeGap, articleId: string) => void;
  onDismiss: (gap: KnowledgeGap) => void;
  /**
   * The server has gaps beyond those loaded. Omitted: inferred from the fetch
   * ceiling, as before.
   */
  hasMore?: boolean;
  /** Fetch the next batch of gaps from the server (offered on the last page). */
  onLoadMore?: () => void;
  loadingMore?: boolean;
}) {
  const [page, setPage] = useState(1);
  const openCount = gaps.filter((g) => g.status !== 'resolved').length;
  const totalPages = Math.max(1, Math.ceil(openCount / GAP_PAGE_SIZE));
  // A dismissal can empty the last page, and a refresh can shrink the list:
  // never strand the reader on a page that no longer exists.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // A failed request must never be drawn as four zeros. "No open gaps" and "we
  // could not ask" are opposite answers — one is the best possible state of a
  // knowledge base and the other is a broken screen — and rendering them
  // identically is how a 400 spent a day looking like good news.
  if (error) {
    return (
      <div className={`${CARD_CLASS} px-6 py-8`} style={CARD}>
        <Notice
          tone="danger"
          headline="The gap list could not be loaded"
          body={
            <>
              {error} No figures are shown for this period, because none were
              received — this is not an empty backlog.
            </>
          }
        />
      </div>
    );
  }

  const open = gaps.filter((g) => g.status !== 'resolved');

  // Did the fetch hit its ceiling? If it did, this list is a page and not the
  // backlog, and nothing on screen may claim otherwise.
  //
  // `dashboard.gaps.totalOpen` is deliberately NOT used as the "of Y" total
  // here: the service computes it by calling the same `identifyGaps` with no
  // limit, which falls back to its OWN default of 20 — so it saturates lower
  // than this list does and would under-report it. There is no uncapped
  // open-gap count in the API, so this says what it counted instead of
  // borrowing a number that means something else.
  const capped = hasMore ?? gaps.length >= GAP_FETCH_LIMIT;
  const coverage = dashboard?.gaps?.coverageScore;
  const currentPage = Math.min(page, totalPages);
  const firstIndex = (currentPage - 1) * GAP_PAGE_SIZE;
  const pageRows = open.slice(firstIndex, firstIndex + GAP_PAGE_SIZE);
  const onLastPage = currentPage >= totalPages;

  return (
    <div className="flex flex-col gap-[14px]">
      <GapKpis dashboard={dashboard} gaps={gaps} days={days} capped={capped} />

      {/* The queue floats as one card, like every other panel on this page.
          It used to run full-bleed under an ink strip, which made it the only
          surface here welded to the window edge. */}
      <div className={`${CARD_CLASS} overflow-hidden`} style={CARD}>
        {/* Defining the ways a gap opens is what makes the list trustworthy.
            Without it, "we don't answer this" is an assertion. */}
        <div className="px-5 pb-[14px] pt-[18px]">
          <h2
            className="text-[20px] leading-[1.15] text-[var(--co-ink)]"
            style={{ fontFamily: 'var(--co-font-display)' }}
          >
            Questions this knowledge base couldn&rsquo;t answer
          </h2>
          <p
            className="mt-2 max-w-[88ch] text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]"
            style={{ textWrap: 'pretty' } as React.CSSProperties}
          >
            Grouped from logged searches. A gap opens when a question returns nothing, or returns
            results nobody opens.
          </p>
        </div>

      {open.length === 0 ? (
        <div className="px-5 pb-8">
          <p className="max-w-[78ch] text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
            No open gaps. Every logged question either returned results someone opened, or has
            already been answered by an article.
          </p>
        </div>
      ) : (
        <div role="table" aria-label="Knowledge gaps">
          <div
            role="row"
            className="flex items-center gap-3 border-b border-[var(--co-line)] px-5 pb-[11px]"
          >
            <ColumnHead>QUESTION</ColumnHead>
            <ColumnHead width={W.times}>TIMES</ColumnHead>
            <ColumnHead width={W.lastAsked}>LAST ASKED</ColumnHead>
            <ColumnHead width={W.action} />
          </div>

          <div role="rowgroup">
            {pageRows.map((gap) => {
              const band = bandOf(gap.priority);
              // The failure mode decides the verb, and the client does not
              // guess: an existing-but-ignored article needs three product
              // names added, not a second article on the same subject. Offering
              // "create" for both is how a KB grows duplicates.
              const covers = gap.relatedArticles ?? [];
              const extendTarget = covers[0];

              return (
                <div
                  key={gap.id}
                  role="row"
                  className="flex items-start gap-3 border-b border-[var(--co-line-subtle)] px-5 py-[11px] last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="min-w-0 truncate text-[13.5px] font-semibold text-[var(--co-ink)]">
                        {`“${gap.topic}”`}
                      </span>
                      <PriorityChip band={band} />
                    </div>

                    {/* The evidence line. The per-channel split the artboard
                        shows (`SEARCH 21 · CHAT 9 · VOICE 4`) is not available:
                        `kb_search_logs` records a channel on the way in, but
                        the gap aggregate does not return the breakdown. So the
                        line states what IS known rather than a fabricated
                        split. */}
                    <div className={`${MONO} mt-[5px] truncate text-[10.5px] tracking-[.08em] text-[var(--co-ink-4)]`}>
                      {[
                        `ASKED ${gap.searchCount}×`,
                        gap.firstDetected ? `FIRST SEEN ${shortDate(gap.firstDetected)}` : null,
                        covers.length === 0
                          ? 'ZERO RESULTS'
                          : `${covers.length} ARTICLE${covers.length === 1 ? '' : 'S'} MATCH, NOBODY OPENS THEM`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>

                    <p
                      className="mt-[6px] max-w-[78ch] text-[12.5px] leading-[1.5] text-[var(--co-ink-2)]"
                      style={{ textWrap: 'pretty' } as React.CSSProperties}
                    >
                      {gap.suggestedContent?.trim()
                        ? gap.suggestedContent
                        : covers.length === 0
                          ? 'No article mentions this at all.'
                          : 'Something matches, but readers are not opening it — the existing article is probably mis-titled or does not say this in so many words.'}
                    </p>
                  </div>

                  <div className="flex-none text-right" style={{ width: W.times }}>
                    <div
                      className={`${MONO} text-[17px] font-semibold tabular-nums`}
                      style={{ color: band === 'high' ? 'var(--co-burgundy)' : 'var(--co-ink)' }}
                    >
                      {gap.searchCount}
                    </div>
                    <div className={`${MONO} mt-[2px] text-[9px] tracking-[.1em] text-[var(--co-ink-4)]`}>
                      ASKED
                    </div>
                  </div>

                  <div
                    className={`${MONO} flex-none text-right text-[11px] tabular-nums text-[var(--co-ink-3)]`}
                    style={{ width: W.lastAsked }}
                  >
                    {relative(gap.lastOccurrence)}
                  </div>

                  <div className="flex flex-none items-center justify-end gap-2" style={{ width: W.action }}>
                    <button
                      type="button"
                      onClick={() => onDismiss(gap)}
                      className={`${MONO} flex-none text-[9.5px] tracking-[.1em] text-[var(--co-ink-4)] hover:text-[var(--co-ink)]`}
                    >
                      DISMISS
                    </button>
                    {extendTarget ? (
                      <button
                        type="button"
                        onClick={() => onExtend(gap, extendTarget)}
                        className="flex h-[30px] flex-none items-center whitespace-nowrap border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-ink)]"
                      >
                        Extend an article
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onDraft(gap)}
                        className="flex h-[30px] flex-none items-center whitespace-nowrap bg-[var(--co-ink)] px-3 text-[12.5px] font-semibold text-[var(--co-ink-invert)]"
                      >
                        Draft an article
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* What is on screen versus what exists. A capped list that does not
              say it is capped is a backlog the reader will take for the whole
              one — and the coverage score is the one figure the ink strip
              carried that nothing else on this page reports. */}
          <CardFoot
            left={
              totalPages > 1
                ? `Showing ${firstIndex + 1}–${firstIndex + pageRows.length} of ${open.length}${capped ? '+' : ''} gaps`
                : capped
                  ? `Showing the first ${open.length} gaps`
                  : `Showing all ${open.length} ${open.length === 1 ? 'gap' : 'gaps'}`
            }
            note={
              <span className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
                <span>
                  {[
                    capped ? `More gaps are open than are loaded${onLoadMore ? '' : '; this is the first batch'}.` : null,
                    typeof coverage === 'number' && Number.isFinite(coverage)
                      ? `Coverage score ${Math.round(coverage)} for this period.`
                      : null,
                    'Dismissed gaps are not listed and are not counted above.',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                </span>
                {totalPages > 1 && (
                  <Pager
                    id="gap-queue-pager"
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={open.length}
                    pageSize={GAP_PAGE_SIZE}
                    onPageChange={(p) => setPage(Math.min(Math.max(1, p), totalPages))}
                  />
                )}
                {capped && onLoadMore && onLastPage && (
                  <button
                    type="button"
                    onClick={onLoadMore}
                    disabled={loadingMore}
                    className="flex h-[26px] flex-none items-center whitespace-nowrap rounded-full border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[11.5px] text-[var(--co-ink-2)] hover:border-[var(--co-ink)] disabled:cursor-wait disabled:opacity-60"
                  >
                    {loadingMore ? 'Loading…' : 'Load more gaps'}
                  </button>
                )}
              </span>
            }
          />
        </div>
      )}
      </div>
    </div>
  );
}

export default GapQueue;
